import React, { useEffect, useState } from 'react';
import { Route, useHistory, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery } from 'react-responsive';
import { LinearProgress } from '@material-ui/core';
import jwtDecode from 'jwt-decode';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { authActions } from '../../store/auth-reducer';
import { storeContext } from '../provider/Provider';
import StaticSidebar from '../static-sidebar/StaticSidebar';
import { websiteSwitchActions } from '../../store/website-switch-reducer';
import Home from '../home/Home';
import Leads from '../leads/Leads';
import PaidMedia from '../paid-media/PaidMedia';
import LeadsFullView from '../leads/LeadsFullView';
import Content from '../content/Content';
import Settings from '../settings/Settings';
import ContentFullView from '../content/ContentFullView';
import ClientCredentials from '../client-credentials/ClientCredentials';
import CallsFullView from '../leads/CallsFullView';
import Support from '../support/Support';
import Analytics from '../analytics/Analytics';
import KeywordsFullView from '../analytics/KeywordsFullView';
import Navbar from '../navbar/Navbar';
import { contentActions } from '../../store/content-reducer';
import AssignedPages from '../content/AssignedPages';
import PaidMediaFullView from '../paid-media/PaidMediaFullView';
import AnalyticsGoogleMyBusiness from '../analytics/AnalyticsGoogleMyBusiness';
import AnalyticsGoogleMyBusinessLocationsFullView from '../analytics/AnalyticsGoogleMyBusinessLocationsFullView';
import CampaignUpdates from '../campaign-updates/CampaignUpdates';
import { campaignUpdatesActions } from '../../store/campaign-updates-reducer';
import Opportunities from '../opportunities/Opportunities';
import CallDetails from '../call-details/CallDetails';
import DigitalPR from '../digital-pr/DigitalPR';
import Backlinks from '../backlink/Backlink';
import ActiveCampaignsFullView from '../digital-pr/ActiveCampaignsFullView';
import PremiumBacklink from '../premium-backlink/PremiumBacklink';
import Yext from '../yext/Yext';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    overflow: 'hidden',
    marginBottom: 60,
  },
  toolbar: theme.mixins.toolbar,
  colorPrimary: {
    backgroundColor: '#E5E5E5',
  },
  barColorPrimary: {
    backgroundColor: '#00B5E2',
  },
}));

const StaticLayout = () => {
  
  const history = useHistory();
  const { domain: domainName } = useParams();
  const [cookies, setCookie] = useCookies(['auth']);
  const [websiteCookie, setWebsiteCookie] = useCookies(['websiteSwitch']);
  const { state, dispatch } = React.useContext(storeContext);
  const classes = useStyles();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const location = useLocation();
  const [currentWebsite, setCurrentWebsite] = useState(null);
  const [currentWebsiteCredentials, setCurrentWebsiteCredentials] = useState(null);
  const [hasAccessGranted, setHasAccessGranted] = useState(false);
  const [dataLayerInitialized, setDataLayerInitialized] = useState(false);

  if(!cookies.id_token && window.location.href.indexOf("#") > 0) {
    const [hash, query] =  window.location.href.split('#')[1].split('?');
    const auth0IdToken = hash?.split('=')[1];
    
    const date = new Date();
    
    const monthlyExpirationDate = new Date(date.setMonth(date.getMonth() + 1));
    
    if (auth0IdToken.length) {
      setCookie('id_token', auth0IdToken, {
        path: '/',
        expires: monthlyExpirationDate,
      });
      dispatch({
        type: authActions.SET_LOGGED_IN,
        payload: {
          isLoggedIn: true,
        },
      });
      history.push('/');
      if(query)
        history.push('/');
    };
  };

  const getMessages = () => {
    
    if (!window.location.pathname.includes('campaign-updates')) {
      axios
        .get(`${REACT_APP_FLASK_API_URL}/v1/loom/${currentWebsite?.name}`)
        .then((messagesResponse) => {
          if (!messagesResponse.data.error) {
            
            const notSeenMessages = messagesResponse.data.data.messages.filter(
              (message) => !message.is_seen_by_client
            );
            if (notSeenMessages?.length) {
              dispatch({
                type: campaignUpdatesActions.CAMPAIGN_UPDATES_SET_ALL,
                payload: {
                  isFetching: false,
                  messages: notSeenMessages,
                  newMessagesCount: notSeenMessages.length,
                },
              });
            }
          }
        })
        .catch(() => {})
    };
  };

  const getClientCredentials = () => {
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/client-credentials/${currentWebsite?.name}`)
      .then((response) => {
        setCurrentWebsiteCredentials(response.data?.data?.credentials);
        dispatch({
          type: websiteSwitchActions.SET_CURRENT_WEBSITE_CREDENTIALS,
          payload: { currentWebsiteCredentials: response.data?.data?.credentials },
        });
      })
      .catch(() => {})
  };

  const switchWebsite = (website, budget) => {
    dispatch({
      type: websiteSwitchActions.SET_CURRENT_WEBSITE,
      payload: { 
        currentWebsite: website, 
        currentWebsiteCredentials: null,
      },
    });
    const date = new Date();
    const monthlyExpirationDate = new Date(date.setMonth(date.getMonth() + 1));
    setWebsiteCookie('website', website, {
      path: '/',
      expires: monthlyExpirationDate,
    });
    setWebsiteCookie('budget', budget, {
      path: '/',
      expires: monthlyExpirationDate,
    });
  }

  const fetchCurrentSiteData = () => {
    if (!currentWebsite)
      return;

    getClientCredentials();
    getMessages();
    
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/assign-page-feature/${currentWebsite?.name}`)
      .catch(() => {})
      .then((resIsEnabled) => {
        if (!resIsEnabled.data.error && resIsEnabled.data.data) {
          dispatch({
            type: websiteSwitchActions.WEBSITE_SWITCH_SET_ALL,
            payload: {
              currentWebsiteSettings: {
                assignPageFeature: resIsEnabled.data.data.assign_page_feature,
              },
            },
          });
          if (resIsEnabled.data.data.assign_page_feature === true) {
            axios
              .get(`${REACT_APP_FLASK_API_URL}/v1/assigned-pages/${currentWebsite?.name}`)
              .then((resAssignedPages) => {
                if (!resAssignedPages.data.error) {
                  dispatch({
                    type: contentActions.CONTENT_SET_ALL,
                    payload: {
                      assignPageFeature: {
                        total: resAssignedPages.data.data.total,
                        rows: resAssignedPages.data.data.values,
                      },
                    },
                  });
                }
              });
          }
        }
      });
  };

  const getData = (idToken) => {
    const userData = jwtDecode(idToken);
    axios
      .get(`${REACT_APP_FLASK_API_URL}/me`, {
        headers: {
          Authorization: idToken,
        },
      })
      .then((res) => {
        dispatch({
          type: authActions.SET_USER,
          payload: {
            isLoggedIn: true,
            user: {
              name: userData.name,
              email: res.data.email,
              role: res.data.role,
            },
          },
        });
        const date = new Date();
        const monthlyExpirationDate = new Date(date.setMonth(date.getMonth() + 1));
        setCookie('username', res.data.username, {
          path: '/',
          expires: monthlyExpirationDate,
        });
      })
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/hd-platform/me/websites`, {
        headers: {
          Authorization: idToken,
        },
      })
      .then((res) => {
        dispatch({
          type: websiteSwitchActions.SET_ALL_WEBSITES,
          payload: { allWebsites: res.data.websites },
        });
        if (!websiteCookie.website) {
          if (res.data.websites?.length === 0) {
            history.push('/no-websites');
          }

          if (res.data.websites?.length === 1) {
            switchWebsite(res.data.websites[0].name, res.data.websites[0].budget);
            setCurrentWebsite({
              name: res.data.websites[0].name,
              budget: res.data.websites[0].budget
            });
          }
          
          if (res.data.websites?.length > 0 && typeof domainName === 'undefined') {
            history.push('/switch-website');
          }
        } else {
          if(typeof domainName !== 'undefined' && domainName !== state.websiteSwitch.currentWebsite) {
            const website = res.data.websites?.filter(( websiteItem ) => websiteItem.name === domainName );
            if ( website?.length > 0 ) {
              switchWebsite(website[0].name, website[0].budget);
              setCurrentWebsite({
                name: website[0].name,
                budget: website[0].budget
              });
              return;
            }
          };
          switchWebsite(websiteCookie.website, websiteCookie.budget);
          setCurrentWebsite({
            name: websiteCookie.website,
            budget: websiteCookie.budget
          });
        }
      })
      .catch((error) => {
        if(error.toString().includes("Network Error") || error.response?.status === 401){
          dispatch({ type: authActions.LOGOUT });
          history.push('/login');
        }
      });
  };

  useEffect(() => {
      if (!currentWebsite)
        return;
      
      fetchCurrentSiteData();    
  }, [currentWebsite]);

  useEffect(() => {
    if (!currentWebsite)
      return;
    
    setHasAccessGranted(!currentWebsiteCredentials?.filter(item => (item.is_done === false)).length > 0);
}, [currentWebsiteCredentials]);

  
useEffect(() => {

  const resolveVisitorType = (email) => {
    if (email.includes('@hennessey.com')) {
      return 'employee';
    }
    return 'client';
  };

  const { email } = state.auth.user;

  if(dataLayerInitialized) return;
  if(!email) return;
  if(!currentWebsite?.name) return;

  const script = document.createElement('script');

  script.innerHTML = `
    dataLayer = [{
      'client': '${currentWebsite?.name}',
      'visitorType': '${resolveVisitorType(email)}'
    }];
  `;

  const gtmScript = document?.querySelector('script[src*="googletagmanager"]');
  gtmScript?.parentNode?.insertBefore(script, gtmScript);

  setDataLayerInitialized(true);
  
    // eslint-disable-next-line consistent-return
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
    }
  };
  }, [state.auth.user, currentWebsite?.name]);

  useEffect(() => {   
    if (cookies.id_token) {
      axios.defaults.headers.common.Authorization = cookies.id_token;
      getData(cookies.id_token);
    } else {
      dispatch({
        type: websiteSwitchActions.WEBSITE_SWITCH_SET_ALL,
        payload: {
          landedOnLocation: location.pathname,
        },
      });
      history.push('/login');
    }
  }, []);

  if (!state.websiteSwitch.websiteHasBeenChosen || !currentWebsiteCredentials) {
    return (
      <>
        {isMobile ? <Navbar /> : <StaticSidebar hasAccessGranted={false} />}
        <main className={classes.content} data-testid="home_test_id">
          {!isMobile && <div className={classes.toolbar} />}
          <LinearProgress
            style={{ width: '100%' }}
            classes={{
              colorPrimary: classes.colorPrimary,
              barColorPrimary: classes.barColorPrimary,
            }}
          />
        </main>
      </>
    );
  }
  if (currentWebsiteCredentials && (hasAccessGranted === false && (window.location.pathname !== '/support' && window.location.pathname !== '/settings'))) {
    return (
      <>
        {isMobile ? <Navbar /> : <StaticSidebar hasAccessGranted={false} />}
        <main className={classes.content} data-testid="home_test_id">
          {!isMobile && <div className={classes.toolbar} />}
          <ClientCredentials />
        </main>
      </>
    );
  }
  return (
    <>
      {isMobile ? <Navbar /> : <StaticSidebar hasAccessGranted={hasAccessGranted} />}
      <main className={classes.content} data-testid="home_test_id">
        {!isMobile && <div className={classes.toolbar} />}
        {!(state.auth.user.role === 'content_only') && (
          <>
            <Route exact path="/">
              <Home />
            </Route>
            <Route exact path="/campaign-updates">
              <CampaignUpdates />
            </Route>
            <Route exact path="/campaign-updates/open/:message_id">
              <CampaignUpdates />
            </Route>
            <Route exact path="/campaign-updates/open/:domain/:message_id">
              <CampaignUpdates />
            </Route>
            <Route exact path="/opportunities">
              <Opportunities />
            </Route>
            <Route exact path="/opportunities/:call_id">
              <CallDetails />
            </Route>
            <Route exact path="/leads">
              <Leads />
            </Route>
            <Route path="/leads/full-view">
              <LeadsFullView />
            </Route>
            <Route path="/calls/full-view">
              <CallsFullView />
            </Route>
            <Route path="/keywords/full-view">
              <KeywordsFullView />
            </Route>
            <Route exact path="/analytics">
              <Analytics />
            </Route>
            <Route exact path="/backlinks">
              <Backlinks />
            </Route>
            {process.env.REACT_APP_ENABLE_PREMIUM_BACKLINK === 'true' && (
              <Route exact path="/premium-backlinks">
                <PremiumBacklink />
              </Route>
            )}
            {process.env.REACT_APP_ENABLE_YEXT === 'true' && (
              <Route exact path="/yext">
                <Yext />
              </Route>
            )}
            <Route exact path="/analytics/gmb">
              <AnalyticsGoogleMyBusiness />
            </Route>
            <Route exact path="/analytics/gmb/locations">
              <AnalyticsGoogleMyBusinessLocationsFullView />
            </Route>
            <Route exact path="/paid-media">
              <PaidMedia />
            </Route>
            <Route path="/paid-media/full-view/campaigns">
              <PaidMediaFullView type="campaigns" />
            </Route>
            <Route path="/paid-media/full-view/locations">
              <PaidMediaFullView type="locations" />
            </Route>
          </>
        )}
        <Route exact path="/content">
          <Content />
        </Route>
        <Route path="/content/assigned-pages">
          <AssignedPages />
        </Route>
        <Route path="/content/full-view/:status?">
          <ContentFullView />
        </Route>
        <Route path="/:domain/content/full-view/:status?">
          <ContentFullView />
        </Route>
        <Route exact path="/digital-pr">
          <DigitalPR />
        </Route>
        <Route exact path="/digital-pr/active-full-view">
          <ActiveCampaignsFullView />
        </Route>
        <Route path="/support">
          <Support />
        </Route>
        <Route path="/settings">
          <Settings />
        </Route>
      </main>
    </>
  );
};

export default StaticLayout;